import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";

const ONDERDEEL_KEYS = {
  groepspringen: "groepspringen",
  "micro-meso": "micro_meso",
  individueel: "individueel",
};

const TOESTEL_LABELS = {
  mt: "Mini-trampoline",
  pegasus: "Pegasus",
  airtrack: "Airtrack",
};

const TOESTEL_ORDER = ["mt", "pegasus", "airtrack"];

export default function Waarden({ onderdeel: routeOnderdeel }) {
  const [data, setData] = useState(null);
  const [sprongen, setSprongen] = useState(null);
  const [q, setQ] = useState("");
  const [activeToestel, setActiveToestel] = useState("mt");
  const onderdeel = ONDERDEEL_KEYS[routeOnderdeel] || routeOnderdeel;

  useEffect(() => {
    api.get("/reglement/waarden").then((r) => setData(r.data));
    api.get("/reglement/sprongen", { params: { onderdeel } }).then((r) => setSprongen(r.data));
    setActiveToestel("mt");
    setQ("");
  }, [onderdeel]);

  const availableToestellen = useMemo(() => {
    if (!sprongen) return TOESTEL_ORDER;
    return TOESTEL_ORDER.filter((k) => Array.isArray(sprongen[k]) && sprongen[k].length > 0);
  }, [sprongen]);

  if (!data) return <div className="text-muted-foreground">Laden...</div>;
  const block = data[onderdeel];
  if (!block) return <div>Sectie niet gevonden.</div>;

  const filterSprong = (list) => {
    if (!q.trim()) return list;
    const ql = q.toLowerCase();
    return list.filter((s) => {
      const hay = (s.naam + " " + (s.afk || []).join(" ") + " " + (s.syn || []).join(" ")).toLowerCase();
      return hay.includes(ql);
    });
  };

  const activeList = (sprongen && sprongen[activeToestel]) || [];
  const filteredActive = filterSprong(activeList);

  return (
    <div data-testid={`waarden-${onderdeel}`}>
      <div className="text-xs uppercase tracking-[0.22em] text-orange-600 font-semibold">Reglement</div>
      <h1 className="heading-display text-4xl sm:text-5xl mt-1">{block.label} — Waarden</h1>
      <p className="text-slate-600 mt-3 mb-8 max-w-2xl">{block.description}</p>

      <div className="space-y-8">
        {Object.entries(block.leeftijden).map(([leeftijdKey, leeftijd]) => (
          <section key={leeftijdKey} className="gs-card p-6" data-testid={`waarden-leeftijd-${leeftijdKey}`}>
            <h2 className="text-lg font-bold mb-4">{leeftijd.label}</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="text-left border-b-2 border-slate-200">
                    <th className="py-2 pr-4 font-semibold">Niveau</th>
                    {Object.keys(Object.values(leeftijd.niveaus)[0]).filter(k => k !== 'note').map((toestel) => (
                      <th key={toestel} className="py-2 pr-4 font-semibold">{TOESTEL_LABELS[toestel] || toestel}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(leeftijd.niveaus).map(([nivKey, niv]) => (
                    <tr key={nivKey} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-3 pr-4 font-mono font-semibold">{nivKey}</td>
                      {Object.entries(niv).filter(([k]) => k !== 'note').map(([toestel, range]) => (
                        <td key={toestel} className="py-3 pr-4 font-mono">{formatRange(range)}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        ))}
      </div>

      {sprongen && (
        <div className="mt-12">
          <h2 className="heading-display text-2xl mb-1">D-waarden per sprong</h2>
          <p className="text-muted-foreground text-sm mb-4">
            Officiële D-waarden uit het reglement — per toestel apart. Let op: een <em>arabier</em> op Airtrack
            heeft een andere D-waarde dan op Pegasus.
          </p>

          {/* Toestel-tabs */}
          <div className="flex flex-wrap gap-2 mb-5" role="tablist" data-testid="toestel-tabs">
            {availableToestellen.map((key) => {
              const isActive = key === activeToestel;
              return (
                <button
                  key={key}
                  onClick={() => setActiveToestel(key)}
                  role="tab"
                  aria-selected={isActive}
                  data-testid={`toestel-tab-${key}`}
                  className={
                    "px-4 py-2 rounded-full text-sm font-semibold border transition-colors " +
                    (isActive
                      ? "bg-orange-500 text-white border-orange-500 shadow-sm"
                      : "bg-card text-foreground border-input hover:border-orange-400 hover:text-orange-600")
                  }
                >
                  {TOESTEL_LABELS[key] || key}
                  <span className={"ml-2 text-xs font-mono " + (isActive ? "opacity-80" : "text-muted-foreground")}>
                    {(sprongen[key] || []).length}
                  </span>
                </button>
              );
            })}
          </div>

          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={`Zoek sprong in ${TOESTEL_LABELS[activeToestel] || activeToestel}...`}
            className="w-full max-w-md px-4 py-2.5 border border-input rounded-lg bg-card mb-5 focus:outline-none focus:ring-2 focus:ring-orange-500"
            data-testid="sprong-search"
          />

          <section
            key={activeToestel}
            className="gs-card p-5"
            data-testid={`sprong-tabel-${activeToestel}`}
          >
            <div className="flex items-baseline justify-between mb-4">
              <h3 className="heading-display text-xl">{TOESTEL_LABELS[activeToestel] || activeToestel}</h3>
              <span className="text-xs text-muted-foreground font-mono">
                {filteredActive.length} / {activeList.length} sprongen
              </span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left border-b-2 border-border">
                    <th className="py-2 pr-4 font-semibold">Sprong</th>
                    <th className="py-2 pr-4 font-semibold w-20">D-waarde</th>
                    <th className="py-2 pr-4 font-semibold">K-code / afkorting</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredActive.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="py-6 text-center text-muted-foreground text-sm">
                        Geen sprongen gevonden{q ? ` voor "${q}"` : ""}.
                      </td>
                    </tr>
                  ) : (
                    filteredActive.map((s) => (
                      <tr key={s.naam} className="border-b border-border/40">
                        <td className="py-2 pr-4">{s.naam}</td>
                        <td className="py-2 pr-4 font-mono font-bold text-orange-600">{s.d?.toFixed(1)}</td>
                        <td className="py-2 pr-4 text-xs font-mono text-muted-foreground">
                          {(s.afk || []).slice(0, 4).join(" · ")}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}

function formatRange(r) {
  if (!r || typeof r !== "object") return "-";
  if (r.geen_max) return "Geen max";
  if (r.min != null && r.max != null) return `${r.min} – ${r.max}`;
  if (r.min != null) return `min ${r.min}`;
  if (r.max != null) return `max ${r.max}`;
  return "—";
}
